/**
 * Sprint V36 (Sa 14.09.2026) — Steckbrief-Kachel im neuen Design (D+A-Kombi).
 *
 * Ablösung der monotonen „Row"-Kachel-Optik durch:
 *  1. Kern-Zitat-Anker oben (amber-Akzent, Serif-nah, redaktioneller Take-Away)
 *  2. Icon-Chip-Grid für kurze Items (max 4-5 Chips à 1-4 Wörter)
 *  3. Prosa-Fallback + Expand-Toggle für lange Items (>65 Zeichen)
 *  4. Fokustyp-Akzentfarbe subtil im Header + im „accent-line" oben
 *  5. Volle Dark-Mode-Unterstützung, Mobile-first (375px)
 *
 * Design-Spec: /app/design_guidelines.json
 * Verwendet auf 3 Routen: /mein-muster (FokustypSpiegel), /andere-verstehen/[slug]
 * (DashboardFokustypDetail), /dashboard/steckbriefe (FokustypSteckbriefe).
 */
import React, { useMemo, useState } from "react";
import { ChevronDown, Quote, Share2, Check } from "lucide-react";
import { API } from "@/lib/api";
import { getChipIconFor, getCategoryIconFor, getCategoryDefaults } from "@/lib/steckbriefIcons";
import { getAccentTheme } from "@/lib/fokustypTheme";

const CHIP_MAX_CHARS = 65;

/**
 * @param {object} p
 * @param {string} p.category                z.B. "verhaltensweisen"
 * @param {string} [p.title]                 Override fuer Kachel-Titel
 * @param {string} p.slug                    Fokustyp-slug (katze/eule/biene/hund)
 * @param {string[]} p.items                 Liste der Bullets/Items
 * @param {string} [p.quote]                 Optionales Kern-Zitat
 * @param {string} [p.quoteAuthor]           Optionale Zitat-Attribution
 * @param {React.ReactNode} [p.footer]       Optionale Erweiterung (z.B. StressmusterGrid)
 * @param {"amber"|"fokustyp"} [p.accentDot] Farbe der Chip-Dots (default: fokustyp)
 * @param {string} [p.testId]                data-testid der Kachel
 * @param {string} [p.description]           Sub-Text unter dem Titel
 */
export default function SteckbriefKachel({
  category,
  title,
  slug,
  items = [],
  quote = null,
  quoteAuthor = null,
  footer = null,
  accentDot = "fokustyp",
  testId,
  description = null,
}) {
  const defaults = getCategoryDefaults(category);
  const CategoryIcon = getCategoryIconFor(category);
  const accent = getAccentTheme(slug);
  const [expanded, setExpanded] = useState(false);
  const [quoteShareState, setQuoteShareState] = useState("idle");

  async function handleShareQuote() {
    if (!quote || !slug) return;
    setQuoteShareState("loading");
    // Sprint V38 — kopiere „Zitat" + Landing-URL mit optionalem Ref-Code in Clipboard
    let refCode = null;
    try {
      // referral_code aus /user/dashboard-Sicht — cheap fetch (cached vom Browser)
      const r = await fetch(`${API}/user/dashboard`, { credentials: "include" });
      if (r.ok) {
        const j = await r.json();
        refCode = j?.user?.referral_code || null;
      }
    } catch { /* silent */ }
    const url = refCode
      ? `https://flex4success.effektiv-kommunizieren.de/?ref=${encodeURIComponent(refCode)}&utm_source=share&utm_medium=user&utm_campaign=v38_kern_zitat`
      : `https://flex4success.effektiv-kommunizieren.de/?utm_source=share&utm_medium=user&utm_campaign=v38_kern_zitat`;
    const text = `„${quote}" — trifft für mich als ${String(slug || "").toUpperCase()} genau ins Schwarze. Wie tickst du? ${url}`;
    try {
      if (navigator.share) {
        await navigator.share({ text, url });
      } else {
        await navigator.clipboard.writeText(text);
      }
      setQuoteShareState("done");
      // Fire-and-forget tracking
      try {
        await fetch(`${API}/share/track`, {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fokustyp: slug, channel: navigator.share ? "native" : "copy_link",
            context: "kern_zitat", variant: `zitat_${category}`,
            has_referral: Boolean(refCode),
          }),
        });
      } catch { /* silent */ }
    } catch (err) {
      if (err && err.name === "AbortError") { setQuoteShareState("idle"); return; }
      setQuoteShareState("idle");
    }
    setTimeout(() => setQuoteShareState("idle"), 2500);
  }

  const { chips, prosa } = useMemo(() => {
    const c = [];
    const p = [];
    (items || []).forEach((it) => {
      const text = typeof it === "string" ? it : String(it || "");
      if (text.length <= CHIP_MAX_CHARS) c.push(text);
      else p.push(text);
    });
    return { chips: c, prosa: p };
  }, [items]);

  const showExpandToggle = prosa.length > 2;
  const visibleProsa = showExpandToggle && !expanded ? prosa.slice(0, 2) : prosa;

  const dotClass =
    accentDot === "amber"
      ? "bg-amber-500 dark:bg-amber-400"
      : "bg-[var(--fts-primary-bright)]";

  return (
    <article
      className={`relative w-full rounded-2xl border ${accent.cardBorder} bg-white dark:bg-slate-900/90 p-5 sm:p-6 lg:p-7 shadow-sm hover:shadow-md hover:border-slate-300 dark:hover:border-slate-700 transition-shadow duration-300 overflow-hidden`}
      data-testid={testId || `steckbrief-kachel-${category}`}
      data-category={category}
      data-slug={slug || ""}
    >
      {/* Accent-Top-Line (Fokustyp-Farbe) */}
      <div
        className={`absolute top-0 left-0 right-0 h-1 ${accent.gradient} opacity-80`}
        aria-hidden="true"
      />

      {/* Header: Icon + Titel + Kategorie-Badge */}
      <header className="flex items-center justify-between gap-3 pb-4 border-b border-slate-100 dark:border-slate-800/80 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <span
            className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${accent.iconBg} ${accent.iconBorder}`}
            aria-hidden="true"
          >
            <CategoryIcon className={`w-5 h-5 ${accent.iconText}`} />
          </span>
          <div className="min-w-0">
            <h3
              className="text-base sm:text-lg font-bold tracking-tight text-slate-900 dark:text-slate-100 truncate"
              data-testid={`kachel-header-title-${category}`}
            >
              {title || defaults.label}
            </h3>
            {description && (
              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400 line-clamp-2">
                {description}
              </p>
            )}
          </div>
        </div>
      </header>

      {/* Kern-Zitat-Anker (optional) */}
      {quote && (
        <div
          className="relative mb-5 p-4 rounded-xl bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent border-l-4 border-amber-500 dark:border-amber-400 dark:from-amber-400/10 dark:via-amber-400/5"
          data-testid={`kachel-kern-zitat-${category}`}
        >
          <Quote
            className="absolute top-2 right-2 text-amber-500/25 dark:text-amber-400/25 w-5 h-5"
            aria-hidden="true"
          />
          <p className="text-sm sm:text-base font-medium italic text-slate-800 dark:text-slate-100 leading-snug pr-6">
            „{quote}"
          </p>
          {quoteAuthor && (
            <p className="mt-1.5 text-[10px] font-semibold tracking-widest uppercase text-amber-600 dark:text-amber-400">
              {quoteAuthor}
            </p>
          )}
          {/* Sprint V38 (So 15.09.2026) — Zitat-Share-Micro-CTA */}
          {slug && (
            <button
              type="button"
              onClick={handleShareQuote}
              disabled={quoteShareState === "loading"}
              data-testid={`kachel-zitat-share-${category}`}
              className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-700 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300 hover:underline underline-offset-2 disabled:opacity-50"
            >
              {quoteShareState === "done" ? (
                <><Check className="w-3 h-3" />Kopiert — teile es jetzt</>
              ) : quoteShareState === "loading" ? (
                <>Kopiere …</>
              ) : (
                <><Share2 className="w-3 h-3" />Zitat teilen</>
              )}
            </button>
          )}
        </div>
      )}

      {/* Chip-Grid (kurze Items) */}
      {chips.length > 0 && (
        <ul
          className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3"
          data-testid={`kachel-chip-grid-${category}`}
        >
          {chips.map((text, i) => {
            const ChipIcon = getChipIconFor(category, text);
            return (
              <li
                key={`${category}-chip-${i}`}
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60 text-sm text-slate-800 dark:text-slate-200 hover:bg-slate-100 hover:border-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-600 transition-colors duration-200"
                data-testid={`kachel-chip-${category}-${i}`}
              >
                {ChipIcon ? (
                  <ChipIcon
                    className={`w-4 h-4 shrink-0 ${accent.iconText}`}
                    aria-hidden="true"
                  />
                ) : (
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${dotClass} shrink-0`}
                    aria-hidden="true"
                  />
                )}
                <span className="leading-snug">{text}</span>
              </li>
            );
          })}
        </ul>
      )}

      {/* Prosa-Fallback (lange Items) */}
      {prosa.length > 0 && (
        <div
          className={`${chips.length > 0 ? "mt-4 pt-4 border-t border-dashed border-slate-200 dark:border-slate-800/80" : ""}`}
          data-testid={`kachel-prosa-section-${category}`}
        >
          <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
            {visibleProsa.map((text, i) => (
              <li key={`${category}-prosa-${i}`} className="flex items-start gap-2">
                <span
                  className={`mt-1.5 h-1 w-1 rounded-full ${dotClass} shrink-0`}
                  aria-hidden="true"
                />
                <span className="leading-snug">{text}</span>
              </li>
            ))}
          </ul>
          {showExpandToggle && (
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--fts-primary-bright)] hover:opacity-80 py-1 px-2 -ml-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors"
              data-testid={`kachel-expand-toggle-${category}`}
              aria-expanded={expanded}
            >
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
                aria-hidden="true"
              />
              {expanded ? "Weniger anzeigen" : `${prosa.length - 2} weitere anzeigen`}
            </button>
          )}
        </div>
      )}

      {/* Footer-Slot (z.B. StressmusterGrid, Deep-Dive-Blocks) */}
      {footer && (
        <div
          className="mt-5 pt-5 border-t border-slate-200 dark:border-slate-700"
          data-testid={`kachel-footer-${category}`}
        >
          {footer}
        </div>
      )}
    </article>
  );
}
